/**
 * CitationSourcesPanel - Ask Grounded RAG (MVP) doc-level sources panel.
 *
 * Consumes the SSE `{type:'sources'}` doc-level payload (see
 * docs/superpowers/specs/2026-06-01-ask-grounded-rag-design.md §11) and renders
 * the retrieved van ban side-by-side with the answer.
 *
 *   - Desktop (>=1120px, F-U-14): a docked complementary rail on the right
 *     edge; the /ask centered answer column shifts left to make room.
 *   - Mobile (<1120px, F-U-15): a collapsible bottom-sheet with a handle.
 *
 * Each source row:
 *   - doc_num + title (Source Serif heading)
 *   - doc_type_name + agency_name
 *   - eff_status rendered by the canonical EffStatusBadge (HHL / HHL1P carry
 *     the multi-cue warning icon + border + "canh bao" aria; TNHL is blue and
 *     NOT a warn per the canonical eff-status table).
 *   - FTS5 snippet (truncated by CSS)
 *   - "Xem van ban goc" link -> /van-ban/:doc_id (corpus detail route).
 *
 * Empty / absent sources -> renders nothing (panel hidden gracefully). This is
 * SAFE to land while backend grounding is flag-gated: when no `sources` event
 * is emitted the consumer passes an empty array and the panel disappears.
 *
 * CAVEAT F-9 (legal-upl): this panel MUST NOT replace or hide the per-message
 * AI-transparency + 4-luat disclaimer. It renders as a SIBLING overlay/sheet,
 * never inside the disclaimer slot, so the disclaimer keeps its layout flow.
 *
 * VN copy: full diacritics per F-FE-CHAT AC-21. No new npm deps (inline SVG +
 * existing primitives) per Lighthouse SSG bundle cap.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { GroundedSourceDoc } from '../../lib/chat-api';
import { EffStatusBadge } from '../corpus/EffStatusBadge';

/**
 * Scoped styles for the panel. Inlined in the component (not chat.css) so the
 * lightweight /ask route does not pull the 2.5k-line chat.css. Design tokens
 * locked per BRAND.md s4: navy #1B3B6F, amber #F5B800, teal #2EA6A6,
 * radius 12px card, spacing 4/8/16/24. Mobile-first 360px, scale to a docked
 * right rail >=1080px. Dark mode via prefers-color-scheme. WCAG 2.1 AA:
 * focus ring amber 2px, eff-status warn carries icon + bold (multi-cue), all
 * text >= 4.5:1.
 */
const PANEL_CSS = `
.asklaw-cite-sources {
  --acs-navy: #1B3B6F;
  --acs-amber: #F5B800;
  --acs-teal: #2EA6A6;
  --acs-surface: #ffffff;
  --acs-border: #e2e8f0;
  --acs-text: #0f172a;
  --acs-muted: #475569;
  --acs-snippet-bg: #f8fafc;
  font-family: Inter, system-ui, sans-serif;
  box-sizing: border-box;
}
.asklaw-cite-sources *, .asklaw-cite-sources *::before, .asklaw-cite-sources *::after {
  box-sizing: border-box;
}
/* Mobile-first: bottom-sheet anchored to viewport bottom (F-U-15). */
.asklaw-cite-sources {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  background: var(--acs-surface);
  border-top: 2px solid var(--acs-navy);
  box-shadow: 0 -8px 24px -8px rgba(15, 23, 42, 0.25);
  border-radius: 16px 16px 0 0;
  max-height: 72vh;
  display: flex;
  flex-direction: column;
}
.asklaw-cite-sources__handle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  background: transparent;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  color: var(--acs-navy);
  font-family: "Source Serif Pro", Georgia, serif;
  font-size: 15px;
  font-weight: 600;
  min-height: 48px;
  text-align: left;
}
.asklaw-cite-sources__handle:focus-visible {
  outline: 2px solid var(--acs-amber);
  outline-offset: -2px;
  border-radius: 12px;
}
.asklaw-cite-sources__header { display: none; }
.asklaw-cite-sources__list {
  list-style: none;
  margin: 0;
  padding: 0 16px 20px;
  overflow-y: auto;
  display: none;
  gap: 12px;
  flex-direction: column;
}
.asklaw-cite-sources--sheet-open .asklaw-cite-sources__list { display: flex; }
.asklaw-cite-sources__row {
  border: 1px solid var(--acs-border);
  border-radius: 12px;
  padding: 12px 14px;
  background: var(--acs-surface);
}
.asklaw-cite-sources__docnum {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--acs-teal);
}
.asklaw-cite-sources__title {
  font-family: "Source Serif Pro", Georgia, serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--acs-navy);
  margin: 2px 0 4px;
  line-height: 1.35;
}
.asklaw-cite-sources__meta {
  font-size: 12.5px;
  color: var(--acs-muted);
  margin-bottom: 6px;
}
.asklaw-cite-sources__eff-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  margin-bottom: 6px;
}
.asklaw-cite-sources__eff-label { color: var(--acs-muted); }
.asklaw-cite-sources__snippet {
  margin: 6px 0 8px;
  padding: 8px 10px;
  background: var(--acs-snippet-bg);
  border-left: 3px solid var(--acs-amber);
  border-radius: 6px;
  font-size: 13px;
  color: var(--acs-text);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.asklaw-cite-sources__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: var(--acs-navy);
  text-decoration: none;
  padding: 4px 0;
  min-height: 32px;
}
.asklaw-cite-sources__link:hover { text-decoration: underline; }
.asklaw-cite-sources__link:focus-visible {
  outline: 2px solid var(--acs-amber);
  outline-offset: 2px;
  border-radius: 4px;
}
/* In-panel open action (PR-4): same affordance as the link, but a real
   <button> that calls onOpenDoc instead of navigating to /van-ban/:id. */
.asklaw-cite-sources__open {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--acs-navy);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  min-height: 32px;
}
.asklaw-cite-sources__open:hover { text-decoration: underline; }
.asklaw-cite-sources__open:focus-visible {
  outline: 2px solid var(--acs-amber);
  outline-offset: 2px;
  border-radius: 4px;
}
/* Desktop >=1120px: side-by-side docked right rail (F-U-14). The /ask route
   adds matching right margin to its centered column at the same breakpoint. */
@media (min-width: 1120px) {
  .asklaw-cite-sources {
    left: auto;
    right: 0;
    top: 64px;
    bottom: 0;
    width: 360px;
    max-height: none;
    border-top: none;
    border-left: 2px solid var(--acs-navy);
    border-radius: 0;
    box-shadow: -8px 0 24px -12px rgba(15, 23, 42, 0.2);
  }
  .asklaw-cite-sources__handle { display: none; }
  .asklaw-cite-sources__header {
    display: block;
    padding: 16px 16px 8px;
    border-bottom: 1px solid var(--acs-border);
  }
  .asklaw-cite-sources__heading {
    font-family: "Source Serif Pro", Georgia, serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--acs-navy);
    margin: 0;
  }
  .asklaw-cite-sources__hint {
    font-size: 12.5px;
    color: var(--acs-muted);
    margin: 4px 0 0;
    line-height: 1.4;
  }
  .asklaw-cite-sources__list {
    display: flex;
    padding: 12px 16px 24px;
  }
}
/* EMBEDDED mode (PR-4 followup, Issue 1): when CitationSourcesPanel is hosted
   INSIDE LawDocPanel (onOpenDoc passed) the host owns the docked panel chrome.
   This panel must then render in NORMAL FLOW - static, full width of its
   container - and NOT self-dock fixed. Otherwise TWO fixed-position panels pin
   to the viewport right edge and the source cards overlap/stack on each other.
   These rules win because they are declared AFTER the base + media-query rules
   above; the embedded modifier intentionally drops the fixed / bottom-sheet /
   1120px dock chrome and the sheet toggle handle (always-expanded in-flow). */
.asklaw-cite-sources--embedded {
  position: static;
  left: auto;
  right: auto;
  bottom: auto;
  width: 100%;
  max-height: none;
  z-index: auto;
  background: transparent;
  border-top: none;
  border-left: none;
  border-radius: 0;
  box-shadow: none;
}
.asklaw-cite-sources--embedded .asklaw-cite-sources__handle { display: none; }
.asklaw-cite-sources--embedded .asklaw-cite-sources__header {
  display: block;
  padding: 4px 16px 8px;
  border-bottom: 1px solid var(--acs-border);
}
.asklaw-cite-sources--embedded .asklaw-cite-sources__heading {
  font-family: "Source Serif Pro", Georgia, serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--acs-navy);
  margin: 0;
}
.asklaw-cite-sources--embedded .asklaw-cite-sources__hint {
  font-size: 12.5px;
  color: var(--acs-muted);
  margin: 4px 0 0;
  line-height: 1.4;
}
/* Cards always shown + stacked vertically in normal flow (no sheet toggle). */
.asklaw-cite-sources--embedded .asklaw-cite-sources__list {
  display: flex;
  padding: 12px 16px 16px;
}
/* Dark mode. */
@media (prefers-color-scheme: dark) {
  .asklaw-cite-sources {
    --acs-surface: #0f172a;
    --acs-border: #334155;
    --acs-text: #e2e8f0;
    --acs-muted: #cbd5e1;
    --acs-snippet-bg: #1e293b;
    --acs-navy: #93b4e8;
    --acs-teal: #5fd0d0;
  }
  .asklaw-cite-sources__row { background: #111c33; }
}
@media (prefers-reduced-motion: reduce) {
  .asklaw-cite-sources, .asklaw-cite-sources * { transition: none !important; }
}
`;

function IconExternal(): ReactElement {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M10 14L21 3" />
    </svg>
  );
}

function IconChevron({ open }: { open: boolean }): ReactElement {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function SourceRow({
  doc,
  onOpenDoc,
}: {
  doc: GroundedSourceDoc;
  onOpenDoc?: (doc: GroundedSourceDoc) => void;
}): ReactElement {
  const metaParts = [doc.doc_type_name, doc.agency_name].filter(Boolean);
  return (
    <li className="asklaw-cite-sources__row">
      <div className="asklaw-cite-sources__docnum">{doc.doc_num}</div>
      <h4 className="asklaw-cite-sources__title">{doc.title}</h4>
      {metaParts.length > 0 && (
        <div className="asklaw-cite-sources__meta">{metaParts.join(' - ')}</div>
      )}
      <div className="asklaw-cite-sources__eff-line">
        <span className="asklaw-cite-sources__eff-label">Tình trạng hiệu lực:</span>
        <EffStatusBadge status={doc.eff_status_code} size="sm" />
      </div>
      {doc.snippet && (
        <blockquote className="asklaw-cite-sources__snippet">{doc.snippet}</blockquote>
      )}
      {onOpenDoc ? (
        // PR-4: in-panel open. Host (LawDocPanel) opens the full text in a new
        // tab on the right rail instead of navigating away to the corpus route.
        // Issue 2: opens the full text IN-PANEL (a new tab on the right rail),
        // NOT a navigate-away/external link -> no external-link arrow icon.
        <button
          type="button"
          className="asklaw-cite-sources__open"
          onClick={() => onOpenDoc(doc)}
          aria-label={`Mở toàn văn: ${doc.doc_num} ${doc.title}`}
        >
          <span>Mở toàn văn</span>
        </button>
      ) : (
        <Link
          to={`/van-ban/${encodeURIComponent(doc.doc_id)}`}
          className="asklaw-cite-sources__link"
          aria-label={`Xem văn bản gốc: ${doc.doc_num} ${doc.title}`}
        >
          <span>Xem văn bản gốc</span>
          <IconExternal />
        </Link>
      )}
    </li>
  );
}

export type CitationSourcesPanelProps = {
  docs: GroundedSourceDoc[];
  /**
   * PR-4 part 2: when provided, the per-doc primary action becomes an in-panel
   * "Mở toàn văn" button that calls onOpenDoc(doc) (used by LawDocPanel to open
   * the full text in a right-rail tab). When ABSENT the behavior is UNCHANGED:
   * the action is the navigate-away "Xem văn bản gốc" Link to /van-ban/:doc_id
   * (the /ask route + any other caller passes no onOpenDoc).
   */
  onOpenDoc?: (doc: GroundedSourceDoc) => void;
};

export function CitationSourcesPanel({
  docs,
  onOpenDoc,
}: CitationSourcesPanelProps): ReactElement | null {
  // EMBEDDED mode (Issue 1): when hosted inside LawDocPanel (onOpenDoc passed),
  // LawDocPanel owns the docked panel chrome. We then render in NORMAL FLOW
  // (static, full width, always expanded) with NO bottom-sheet behavior - no
  // self-dock, no sheet toggle handle - so we do not double-dock fixed and
  // overlap the host panel's cards.
  const embedded = typeof onOpenDoc === 'function';

  // Standalone (/ask route) mobile bottom-sheet starts collapsed (handle
  // visible). Desktop rail is always expanded via CSS. Embedded mode ignores
  // this (always expanded, in-flow). We track only the mobile open state here.
  const [sheetOpen, setSheetOpen] = useState(false);

  // Collapse the sheet again whenever the source set changes (new answer).
  useEffect(() => {
    setSheetOpen(false);
  }, [docs]);

  // Empty / absent -> hide gracefully (flag-gated backend may not emit any).
  if (!docs || docs.length === 0) return null;

  const count = docs.length;
  const countLabel = `Văn bản trích dẫn (${count})`;

  // Phan 2B (BUG-2026-06-15 review): the panel now receives ONLY the docs the
  // answer cites (applyCitedDocIds filters to citedSourceDocIds), so it maps 1-1
  // with the answer's "Van ban tham khao" footer. No retrieved-but-not-cited
  // group, no per-row badge (every row IS cited) - render the list directly.

  // In embedded mode the list is always shown (no sheet toggle); standalone
  // mobile keeps the collapse/expand handle. The embedded modifier class drops
  // all fixed/dock/sheet chrome via CSS (declared after the base rules).
  const rootClass = embedded
    ? 'asklaw-cite-sources asklaw-cite-sources--embedded asklaw-cite-sources--sheet-open'
    : sheetOpen
      ? 'asklaw-cite-sources asklaw-cite-sources--sheet-open'
      : 'asklaw-cite-sources';

  return (
    <aside
      className={rootClass}
      role="complementary"
      aria-label="Văn bản trích dẫn"
    >
      <style>{PANEL_CSS}</style>
      {/* Sheet toggle handle: standalone (/ask mobile) only. In embedded mode
          LawDocPanel owns the panel chrome -> no handle, always expanded. */}
      {!embedded && (
        <button
          type="button"
          className="asklaw-cite-sources__handle"
          onClick={() => setSheetOpen((v) => !v)}
          aria-expanded={sheetOpen}
          aria-controls="asklaw-cite-sources-list"
        >
          <span className="asklaw-cite-sources__handle-label">{countLabel}</span>
          <IconChevron open={sheetOpen} />
        </button>
      )}
      <header className="asklaw-cite-sources__header">
        <h3 className="asklaw-cite-sources__heading">{countLabel}</h3>
        <p className="asklaw-cite-sources__hint">
          Các văn bản được trích dẫn trong câu trả lời. Đối chiếu với văn bản gốc
          để xác thực.
        </p>
      </header>
      <ul id="asklaw-cite-sources-list" className="asklaw-cite-sources__list">
        {docs.map((doc) => (
          <SourceRow key={doc.doc_id} doc={doc} onOpenDoc={onOpenDoc} />
        ))}
      </ul>
    </aside>
  );
}

export default CitationSourcesPanel;
